"use client";

import ShapeFactory from "@tosize/shapes/src/lib/factory/ShapeFactory";
import type { SideName } from "@tosize/shapes/src/lib/models/Side/SideName";
import { getMoneyValue } from "@tosize/shapes/src/lib/utils";
import { ProductionUnit } from "@tosize/ui";
import { type FC, type PropsWithChildren, useMemo } from "react";
import useFinishes from "../hooks/useFinishes";
import useProducts from "../hooks/useProducts";
import useShapes from "../hooks/useShapes";
import FinishFactory from "../lib/factory/FinishFactory";
import type Finish from "../lib/models/Finish";
import { usePostDataContext } from "./PostDataContext";
import { PriceContext } from "./PriceContext";

type Props = PropsWithChildren;

export const PriceContextProvider: FC<Props> = ({ children }) => {
	const { data, workpiece, selectedBand } = usePostDataContext();
	const { productData, defaultPanel } = useProducts();
	const { shape } = useShapes();
	const { ungroupedFinishes } = useFinishes();

	const shapeModel = useMemo(
		() =>
			shape && productData ? ShapeFactory.getInstance(shape, data.unit, productData) : undefined,
		[shape, productData, data.unit],
	);

	const shapePrice = useMemo(() => {
		if (shapeModel) {
			return shapeModel.calculatePrice(workpiece.shape_dimensions, 1, data.unit);
		}

		return 0;
	}, [workpiece.shape_dimensions, shapeModel, data.unit]);

	const priceType: "minimum" | "specific" = shapePrice > 0 ? "specific" : "minimum";

	const finishPrice = useMemo(() => {
		let price = 0;

		for (const [sideCode, values] of Object.entries(workpiece.finishes.side)) {
			for (const [finishCode, finishOptions] of Object.entries(values)) {
				// This needs to use the ungrouped finishes because the grouped finishes
				// have merged their finish codes. Which results in missing prices :(
				const finishInput = ungroupedFinishes?.find((f) => f.code === finishCode);

				if (finishInput) {
					const finishModel = FinishFactory.getInstance(finishInput);

					const sideLength =
						shapeModel?.calculateSideLength(sideCode as SideName, workpiece.shape_dimensions) ?? 0;

					const isCustomEdgeBand =
						finishCode === "absband_side" && finishModel.input.options.custom !== undefined;

					if (isCustomEdgeBand && selectedBand && sideLength > 0) {
						const pricePerMM = Number(selectedBand.price);

						const minPurchaseLength =
							finishModel.input.options.custom?.constraints.min_purchase_length_per_side ?? 0;

						const length = Math.max(sideLength, minPurchaseLength) / 1000;

						const extraLengthInMeters = 0.08;
						const extraMarginPercentage = 1.1;

						price += pricePerMM * ((length + extraLengthInMeters) * extraMarginPercentage);
					}

					if (sideLength > 0 || finishCode.startsWith("boorgat")) {
						price += finishModel.calculatePrice(sideLength, finishOptions);
					}
				}
			}
		}

		return price;
	}, [
		selectedBand,
		shapeModel,
		ungroupedFinishes,
		workpiece.finishes.side,
		workpiece.shape_dimensions,
	]);

	const finishSurtax = useMemo(() => {
		let price = 0;

		const uniqueFinishes = new Map<string, Finish>();

		for (const [_sideCode, values] of Object.entries(workpiece.finishes.side)) {
			for (const [finishCode, _finishOptions] of Object.entries(values)) {
				const finishInput = ungroupedFinishes?.find((f) => f.code === finishCode);
				const finishModel = finishInput ? FinishFactory.getInstance(finishInput) : undefined;
				const isCustomEdgeBand =
					finishCode === "absband_side" && finishModel?.input.options.custom !== undefined;

				if (finishInput) {
					const finishModel = FinishFactory.getInstance(finishInput);

					uniqueFinishes.set(
						isCustomEdgeBand ? "absband_side_custom" : finishInput.code,
						finishModel,
					);
				}
			}
		}

		uniqueFinishes.forEach((finishModel, code) => {
			if (
				code === "absband_side_custom" &&
				finishModel.input.options.custom?.constraints.start_fee !== undefined
			) {
				price += getMoneyValue(finishModel.input.options.custom?.constraints.start_fee);
			} else {
				price += getMoneyValue(finishModel.input.price_data.start_fee);
			}
		});

		return price;
	}, [ungroupedFinishes, workpiece.finishes.side]);

	const panelPrice = useMemo(
		() => shapePrice + finishPrice + finishSurtax,
		[shapePrice, finishPrice, finishSurtax],
	);

	const completePanelPrice = useMemo(() => {
		if (defaultPanel && productData && data.unit === ProductionUnit.CompletePanel) {
			const panelSizes = productData.panelSizes?.filter(
				(panel) => panel.length >= defaultPanel.length && panel.width >= defaultPanel.width,
			);

			const moneyComplete = panelSizes?.length
				? panelSizes
						.map((panel) => getMoneyValue(panel.price))
						.reduce((prev, current) => (current < prev ? current : prev), Number.POSITIVE_INFINITY)
				: undefined;

			const priceComplete = moneyComplete ? moneyComplete : 0;

			return (defaultPanel.length / 1000) * (defaultPanel.width / 1000) * priceComplete;
		}

		return 0;
	}, [productData, data.unit, defaultPanel]);

	const isCheckModeCompletePanel = useMemo(
		() => !!productData?.checkModeEnabled && !!completePanelPrice,
		[completePanelPrice, productData?.checkModeEnabled],
	);

	const price = useMemo(
		() => (isCheckModeCompletePanel ? completePanelPrice : panelPrice),
		[isCheckModeCompletePanel, completePanelPrice, panelPrice],
	);

	return (
		<PriceContext.Provider
			value={{
				priceType,
				price,
				isCheckModeCompletePanel,
			}}
		>
			{children}
		</PriceContext.Provider>
	);
};
